import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Download, Music } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const THEME_SONG_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_87e159dc-3189-4727-ae1e-bf728f7f511e/artifacts/7pxiwec3_NDP%20Theam%20Song%20final.mp3";

const ThemeSongPlayer = () => {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoaded = () => setDuration(audio.duration);
    const onEnded = () => { setIsPlaying(false); audio.currentTime = 0; };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) { audio.pause(); } else { audio.play().catch(() => {}); }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const currentTime = audioRef.current?.currentTime || 0;

  if (isMinimized) {
    return (
      <>
        <audio ref={audioRef} src={THEME_SONG_URL} preload="metadata" loop />
        <div
          className="fixed bottom-4 right-4 z-50 bg-party-red text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform animate-pulse-slow"
          onClick={() => setIsMinimized(false)}
          data-testid="theme-song-expand-btn"
          title={t({ en: 'NDP Theme Song', bn: 'এনডিপি থিম সং' })}
        >
          <Music className="h-5 w-5" />
        </div>
      </>
    );
  }

  return (
    <>
      <audio ref={audioRef} src={THEME_SONG_URL} preload="metadata" loop />
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-t border-gray-700 shadow-2xl" data-testid="theme-song-player">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3">
          {/* Song info */}
          <div className="flex items-center gap-2 min-w-0 shrink-0">
            <div className={`w-8 h-8 rounded-full bg-party-red/20 flex items-center justify-center ${isPlaying ? 'animate-spin-slow' : ''}`}>
              <Music className="h-4 w-4 text-party-red" />
            </div>
            <div className="hidden sm:block min-w-0">
              <p className="text-white text-sm font-medium truncate">{t({ en: 'NDP Theme Song', bn: 'এনডিপি থিম সং' })}</p>
              <p className="text-gray-400 text-xs truncate">{t({ en: 'National Democratic Party', bn: 'ন্যাশনাল ডেমোক্রেটিক পার্টি' })}</p>
            </div>
          </div>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="w-9 h-9 rounded-full bg-party-red hover:bg-red-700 flex items-center justify-center text-white transition-colors shrink-0"
            data-testid="theme-song-play-btn"
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>

          {/* Time + Progress */}
          <span className="text-gray-400 text-xs w-10 text-right shrink-0">{formatTime(currentTime)}</span>
          <div className="flex-1 h-1.5 bg-gray-700 rounded-full cursor-pointer group" onClick={handleSeek}>
            <div
              className="h-full bg-party-red rounded-full relative transition-all"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow" />
            </div>
          </div>
          <span className="text-gray-400 text-xs w-10 shrink-0">{formatTime(duration)}</span>

          {/* Volume */}
          <button onClick={toggleMute} className="text-gray-400 hover:text-white transition-colors shrink-0" data-testid="theme-song-mute-btn">
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>

          {/* Download */}
          <a
            href={THEME_SONG_URL}
            download="NDP_Theme_Song.mp3"
            className="text-gray-400 hover:text-party-green transition-colors shrink-0"
            data-testid="theme-song-download-btn"
            title={t({ en: 'Download Theme Song', bn: 'থিম সং ডাউনলোড করুন' })}
          >
            <Download className="h-4 w-4" />
          </a>

          {/* Minimize */}
          <button
            onClick={() => setIsMinimized(true)}
            className="text-gray-500 hover:text-white text-xs transition-colors shrink-0 ml-1"
            data-testid="theme-song-minimize-btn"
          >
            ✕
          </button>
        </div>
      </div>
    </>
  );
};

export default ThemeSongPlayer;
